export type CompanyRole = 'company_admin' | 'editor' | 'viewer';

export type RoleDefaultsMap = Partial<Record<CompanyRole, string[]>>;
export type RoleCapabilitySummary = { can: string[]; cannot: string[] };

type Capability = {
  key: string;
  labelKey: string;
  requiredPermissions: string[];
};

const CAPABILITIES: Capability[] = [
  { key: 'view_dashboard', labelKey: 'CAPABILITIES.VIEW_DASHBOARD', requiredPermissions: ['company.dashboard.read'] },
  { key: 'view_properties', labelKey: 'CAPABILITIES.VIEW_PROPERTIES', requiredPermissions: ['company.properties.read'] },
  { key: 'manage_properties', labelKey: 'CAPABILITIES.MANAGE_PROPERTIES', requiredPermissions: ['company.properties.write'] },
  { key: 'delete_properties', labelKey: 'CAPABILITIES.DELETE_PROPERTIES', requiredPermissions: ['company.properties.delete'] },
  { key: 'view_contacts', labelKey: 'CAPABILITIES.VIEW_CONTACTS', requiredPermissions: ['company.contacts.read'] },
  { key: 'manage_contacts', labelKey: 'CAPABILITIES.MANAGE_CONTACTS', requiredPermissions: ['company.contacts.write'] },
  { key: 'delete_contacts', labelKey: 'CAPABILITIES.DELETE_CONTACTS', requiredPermissions: ['company.contacts.delete'] },
  { key: 'view_documents', labelKey: 'CAPABILITIES.VIEW_DOCUMENTS', requiredPermissions: ['company.property_documents.read'] },
  { key: 'manage_documents', labelKey: 'CAPABILITIES.MANAGE_DOCUMENTS', requiredPermissions: ['company.property_documents.write'] },
  { key: 'delete_documents', labelKey: 'CAPABILITIES.DELETE_DOCUMENTS', requiredPermissions: ['company.property_documents.delete'] },
  { key: 'manage_templates', labelKey: 'CAPABILITIES.MANAGE_TEMPLATES', requiredPermissions: ['company.templates.write'] },
  { key: 'manage_qr_poster_texts', labelKey: 'CAPABILITIES.MANAGE_QR_POSTER_TEXTS', requiredPermissions: ['company.qr_poster_texts.write'] },
  { key: 'manage_company_profile', labelKey: 'CAPABILITIES.MANAGE_COMPANY_PROFILE', requiredPermissions: ['company.company_profile.write'] },
  { key: 'send_announcements', labelKey: 'CAPABILITIES.SEND_ANNOUNCEMENTS', requiredPermissions: ['company.announcements.send'] },
  { key: 'manage_users', labelKey: 'CAPABILITIES.MANAGE_USERS', requiredPermissions: ['company.users.invite', 'company.users.update', 'company.users.deactivate', 'company.users.remove'] },
  { key: 'import_properties', labelKey: 'CAPABILITIES.IMPORT_PROPERTIES', requiredPermissions: ['company.properties.import'] },
];

const CAPABILITY_PRIORITY = [
  'view_dashboard',
  'view_properties',
  'manage_properties',
  'delete_properties',
  'view_contacts',
  'manage_contacts',
  'delete_contacts',
  'view_documents',
  'manage_documents',
  'delete_documents',
  'manage_templates',
  'manage_qr_poster_texts',
  'manage_company_profile',
  'send_announcements',
  'manage_users',
  'import_properties',
];

const CAPABILITY_BY_KEY = CAPABILITIES.reduce((acc, capability) => {
  acc[capability.key] = capability;
  return acc;
}, {} as Record<string, Capability>);

function hasAllPermissions(rolePermissions: Set<string>, requiredPermissions: string[]): boolean {
  return requiredPermissions.every((permission) => rolePermissions.has(permission));
}

export function roleNameKey(role: string | null | undefined): string {
  if (role === 'Condominium Manager') return 'ROLES.CONDOMINIUM_MANAGER';
  if (role === 'Accountant') return 'ROLES.ACCOUNTANT';
  if (role === 'Property Manager') return 'ROLES.PROPERTY_MANAGER';
  if (role === 'Technical Assets Manager') return 'ROLES.TECHNICAL_ASSETS_MANAGER';
  if (role === 'Caretaker') return 'ROLES.CARETAKER';
  if (role === 'company_admin') return 'ROLES.COMPANY_ADMIN';
  if (role === 'editor') return 'ROLES.EDITOR';
  return 'ROLES.VIEWER';
}

export function roleDescriptionKey(role: string | null | undefined): string {
  if (role === 'company_admin') return 'ROLES.DESCRIPTION.COMPANY_ADMIN';
  if (role === 'editor') return 'ROLES.DESCRIPTION.EDITOR';
  return 'ROLES.DESCRIPTION.VIEWER';
}

export function buildRoleCapabilitySummary(
  roleDefaults: RoleDefaultsMap,
  role: string | null | undefined,
  maxCan = 8,
  maxCannot = 3
): RoleCapabilitySummary {
  const normalizedRole: CompanyRole = role === 'company_admin' || role === 'editor' || role === 'viewer'
    ? role
    : 'viewer';
  const rolePermissions = new Set(roleDefaults?.[normalizedRole] || []);

  const can = CAPABILITY_PRIORITY
    .map((capabilityKey) => CAPABILITY_BY_KEY[capabilityKey])
    .filter((capability) => capability && hasAllPermissions(rolePermissions, capability.requiredPermissions))
    .map((capability) => capability.labelKey)
    .slice(0, maxCan);

  const cannot = CAPABILITY_PRIORITY
    .map((capabilityKey) => CAPABILITY_BY_KEY[capabilityKey])
    .filter((capability) => capability && !hasAllPermissions(rolePermissions, capability.requiredPermissions))
    .map((capability) => capability.labelKey)
    .slice(0, maxCannot);

  return { can, cannot };
}
